import { useState } from 'react'
import { useLang } from '../i18n'

export type TimeRangeType = 'this_week' | 'this_month' | 'last_month' | 'this_quarter' | 'custom'

export interface TimeRangeValue {
  range: TimeRangeType
  from?: string  // YYYY-MM-DD, only meaningful when range === 'custom'
  to?: string    // YYYY-MM-DD, only meaningful when range === 'custom'
}

export function defaultTimeRange(): TimeRangeValue {
  return { range: 'this_month' }
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10)
}

function firstOfMonthStr(): string {
  const d = new Date()
  d.setDate(1)
  return d.toISOString().slice(0, 10)
}

interface TimeRangeProps {
  value: TimeRangeValue
  onChange: (value: TimeRangeValue) => void
}

const OPTIONS: TimeRangeType[] = ['this_week', 'this_month', 'last_month', 'this_quarter', 'custom']

const OPTION_KEYS: Record<TimeRangeType, string> = {
  this_week: 'timeRangeThisWeek',
  this_month: 'timeRangeThisMonth',
  last_month: 'timeRangeLastMonth',
  this_quarter: 'timeRangeThisQuarter',
  custom: 'timeRangeCustom',
}

export function TimeRange({ value, onChange }: TimeRangeProps) {
  const { t } = useLang()

  // Internal state to preserve custom dates when switching away and back
  const [customFrom, setCustomFrom] = useState<string>(value.from ?? firstOfMonthStr())
  const [customTo, setCustomTo] = useState<string>(value.to ?? todayStr())

  const handleRangeSelect = (range: TimeRangeType) => {
    if (range === 'custom') {
      onChange({ range, from: customFrom, to: customTo })
    } else {
      onChange({ range })
    }
  }

  const handleFromChange = (from: string) => {
    setCustomFrom(from)
    if (value.range === 'custom') {
      onChange({ range: 'custom', from, to: customTo })
    }
  }

  const handleToChange = (to: string) => {
    setCustomTo(to)
    if (value.range === 'custom') {
      onChange({ range: 'custom', from: customFrom, to })
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      {OPTIONS.map((opt) => (
        <label
          key={opt}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            cursor: 'pointer',
            padding: '4px 10px',
            borderRadius: '4px',
            border: '1px solid #d9d9d9',
            background: value.range === opt ? '#1a1a2e' : '#fff',
            color: value.range === opt ? '#fff' : '#333',
            fontSize: '13px',
            userSelect: 'none',
          }}
        >
          <input
            type="radio"
            name="time-range"
            value={opt}
            checked={value.range === opt}
            onChange={() => handleRangeSelect(opt)}
            style={{ display: 'none' }}
          />
          {t(OPTION_KEYS[opt])}
        </label>
      ))}

      {value.range === 'custom' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '8px' }}>
          <label style={{ fontSize: '13px', color: '#555' }}>{t('timeRangeFrom')}</label>
          <input
            type="date"
            value={customFrom}
            onChange={(e) => handleFromChange(e.target.value)}
            style={{ fontSize: '13px', padding: '3px 6px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
          />
          <label style={{ fontSize: '13px', color: '#555' }}>{t('timeRangeTo')}</label>
          <input
            type="date"
            value={customTo}
            onChange={(e) => handleToChange(e.target.value)}
            style={{ fontSize: '13px', padding: '3px 6px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
          />
        </div>
      )}
    </div>
  )
}
